import cc from 'classcat';
import Image from 'next/image';
import React, { useEffect, memo, useRef, useState } from 'react';

import { KEY_CODES, KEY_DOWN, SHOPIFY_URL } from '../../config/constants';
import css from './faqs.module.scss';
import PlusToMinus from './PlusToMinus';

type FAQ = {
  id: number;
  title: string;
  description: string;
};

type FAQProps = {
  faq: FAQ;
};
const FAQ: React.FC<FAQProps> = ({ faq }) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => {
    setOpen(!open);
  };
  const id = `faq-${faq.id}`;
  return (
    <>
      <button
        aria-controls={id}
        aria-expanded={open ? 'true' : 'false'}
        className={css.faq}
        id={`${id}-label`}
        onClick={toggleOpen}
        type="button"
      >
        {faq.title}
        <PlusToMinus open={open} />
      </button>
      <div
        aria-labelledby={`${id}-label`}
        className={css.faqDescription}
        dangerouslySetInnerHTML={{ __html: faq.description }}
        hidden={!open}
        id={id}
        role="region"
      />
    </>
  );
};

type Props = {
  title: string;
  description: string;
  faqs: FAQ[];
  minimal?: boolean;
};

const FAQs: React.FC<Props> = ({ faqs, title, description, minimal = false }) => {
  const listRef = useRef<HTMLUListElement>(null);
  const [focused, setFocused] = useState(false);

  const onBlur = () => {
    setFocused(false);
  };
  const onFocus = () => {
    setFocused(true);
  };

  const move = (dir: number) => {
    if (!listRef.current) {
      return;
    }

    const focusableElements = [...listRef.current.querySelectorAll('button')];
    const activeElement = document.activeElement as HTMLButtonElement;

    if (dir === 0) {
      focusableElements[0].focus();
      return;
    }

    if (dir === 1000) {
      focusableElements[focusableElements.length - 1].focus();
      return;
    }

    const index = focusableElements.indexOf(activeElement);

    if (index < 0) {
      return;
    }

    let next = index + dir;
    if (next < 0) {
      next = focusableElements.length - 1;
    } else if (next === focusableElements.length) {
      next = 0;
    }

    focusableElements[next].focus();
  };

  useEffect(() => {
    if (!focused) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      switch (event.code) {
        case KEY_CODES.ARROW_DOWN:
          event.preventDefault();
          move(1);
          break;
        case KEY_CODES.ARROW_UP:
          event.preventDefault();
          move(-1);
          break;
        case KEY_CODES.HOME:
          event.preventDefault();
          move(0);
          break;
        case KEY_CODES.END:
          event.preventDefault();
          move(1000);
          break;
        default:
          break;
      }
    };

    document.addEventListener(KEY_DOWN, onKeyDown);

    return () => {
      document.removeEventListener(KEY_DOWN, onKeyDown);
    };
  }, [focused]);

  return (
    <section className={cc([css.root, { [css.padded]: !minimal }])} id="faqs">
      <h2 className={cc([css.title, { [css.minimal]: minimal }])}>
        {minimal ? (
          title
        ) : (
          <Image alt={title} src="/loyalty/faqs.png" height="166" width="283" layout="responsive" objectFit="contain" />
        )}
      </h2>
      <p className={css.description} dangerouslySetInnerHTML={{ __html: description }} />
      <ul className={cc([css.list, { [css.minimal]: minimal }])} onFocus={onFocus} onBlur={onBlur} ref={listRef}>
        {faqs.map((faq) => (
          <li className={css.item} key={faq.id}>
            <FAQ faq={faq} />
          </li>
        ))}
      </ul>
      <div className={css.viewAll}>
        <a href={`${SHOPIFY_URL}pages/questions`}>View All FAQs</a>
      </div>
      <div className={css.terms}>
        By participating in OV Rewards, you agree to the{' '}
        <a href={`${SHOPIFY_URL}pages/terms-of-use#ov-rewards`} target="_blank" rel="noreferrer">
          OV Rewards Terms and Conditions
        </a>
        ,{' '}
        <a href={`${SHOPIFY_URL}pages/privacy-policy`} target="_blank" rel="noreferrer">
          Privacy Policy
        </a>
        , and{' '}
        <a href={`${SHOPIFY_URL}pages/privacy-policy#your-california-privacy-rights`} target="_blank" rel="noreferrer">
          Financial Incentive Notice
        </a>{' '}
        (if a California Resident).
      </div>
    </section>
  );
};
export default memo(FAQs);
